import { useMemo } from 'react';

type Column = {
  key: string;
  width?: string | number;
  minWidth?: number;
  weight?: number;
  sticky?: boolean;
};

export function useColumnsWithWidth<T extends Column>(
  columns: Array<T>,
  width: number | null,
) {
  return useMemo(() => {
    if (!width) return columns;

    const weightedColumns = columns.filter((col) => col.weight !== undefined);
    const totalWeight = weightedColumns.reduce(
      (sum, col) => sum + (col.weight ?? 1),
      0,
    );

    if (totalWeight <= 0) return columns;

    const resolvedWidthByKey = new Map<string, number>();

    for (const col of weightedColumns) {
      resolvedWidthByKey.set(
        col.key,
        Math.max(col.minWidth ?? 0, (width * (col.weight ?? 1)) / totalWeight),
      );
    }

    let left = 0;

    return columns.map((col) => {
      const resolvedWidth = col.width ?? resolvedWidthByKey.get(col.key);

      if (col.sticky && typeof resolvedWidth === 'number') {
        const withSticky = {
          ...col,
          width: resolvedWidth,
          left,
        };

        left += resolvedWidth ?? 0;

        return withSticky;
      }

      return {
        ...col,
        width: resolvedWidth,
      };
    });
  }, [columns, width]);
}
